// Health calculation utilities

/**
 * Calculate Waist-to-Hip Ratio
 */
export const calculateWHR = (waist, hip) => {
  if (!waist || !hip || hip === 0) return 0;
  return (waist / hip).toFixed(2);
};

/**
 * Get WHR risk category
 */
export const getWHRCategory = (whr, gender = "male") => {
  const value = parseFloat(whr);

  if (gender === "male") {
    if (value < 0.9)
      return { level: "Low", color: "green", message: "Healthy range" };
    if (value < 1.0)
      return { level: "Moderate", color: "yellow", message: "Monitor closely" };
    return { level: "High", color: "red", message: "Consult doctor" };
  } else {
    if (value < 0.8)
      return { level: "Low", color: "green", message: "Healthy range" };
    if (value < 0.86)
      return { level: "Moderate", color: "yellow", message: "Monitor closely" };
    return { level: "High", color: "red", message: "Consult doctor" };
  }
};

/**
 * Get visceral fat zone
 */
export const getVisceralFatZone = (visceralFat) => {
  const value = parseFloat(visceralFat);

  if (value < 10) {
    return {
      zone: "Normal",
      color: "green",
      message: "Excellent! Keep up healthy habits",
      tips: ["Maintain current diet", "Continue regular exercise"],
    };
  } else if (value < 15) {
    return {
      zone: "Elevated",
      color: "yellow",
      message: "Consider lifestyle changes",
      tips: [
        "Reduce refined carbs",
        "Increase cardio",
        "Focus on core exercises",
      ],
    };
  } else {
    return {
      zone: "High Risk",
      color: "red",
      message: "Medical consultation recommended",
      tips: ["Consult doctor", "Aggressive cardio", "Strict diet control"],
    };
  }
};

/**
 * Calculate composition score (0-100)
 */
export const calculateCompositionScore = (fat, muscle, bmi, whr) => {
  const fatScore = Math.max(0, 100 - (fat || 0)) * 0.3;
  const muscleScore = (muscle || 0) * 0.3;
  const bmiScore = Math.max(0, (30 - (bmi || 0)) * 2) * 0.2;
  const whrScore = Math.max(0, (1 - parseFloat(whr || 0)) * 100) * 0.2;

  return Math.min(
    100,
    Math.max(0, fatScore + muscleScore + bmiScore + whrScore)
  ).toFixed(0);
};

/**
 * Calculate trend direction from array of values
 */
export const calculateTrend = (values) => {
  if (!values || values.length < 2) return "stable";

  const recent = values.slice(-7); // Last 7 days
  const avg = recent.reduce((sum, val) => sum + val, 0) / recent.length;
  const first = recent[0];
  const last = recent[recent.length - 1];

  const change = ((last - first) / first) * 100;

  if (Math.abs(change) < 1) return "stable";
  return change > 0 ? "up" : "down";
};

/**
 * Calculate percentage change
 */
export const calculatePercentageChange = (oldValue, newValue) => {
  if (!oldValue || oldValue === 0) return 0;
  return (((newValue - oldValue) / oldValue) * 100).toFixed(1);
};

/**
 * Get trend status for a metric
 */
export const getTrendStatus = (metric, trend, isIncreaseBetter = false) => {
  if (trend === "stable") {
    return { status: "Stable", color: "gray", icon: "➡️" };
  }

  const isImproving = isIncreaseBetter ? trend === "up" : trend === "down";

  if (isImproving) {
    return { status: "Improving", color: "green", icon: "✅" };
  } else {
    return { status: "Needs Attention", color: "red", icon: "⚠️" };
  }
};

/**
 * Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor equation
 */
export const calculateBMR = (weight, height, age, gender = "male") => {
  if (!weight || !height || !age) return 0;

  // Mifflin-St Jeor Equation
  let bmr = 10 * weight + 6.25 * height - 5 * age;

  if (gender === "male") {
    bmr += 5;
  } else {
    bmr -= 161;
  }

  return Math.round(bmr);
};

/**
 * Calculate calorie range based on goal
 */
export const calculateCalorieRange = (
  bmr,
  goal = "maintain",
  activityLevel = "moderate"
) => {
  const activityMultipliers = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    veryActive: 1.9,
  };

  const maintenance = Math.round(
    bmr * (activityMultipliers[activityLevel] || 1.55)
  );

  switch (goal) {
    case "weight_loss":
      return {
        min: maintenance - 500,
        max: maintenance - 300,
        aggressive: maintenance - 750,
        maintenance,
      };
    case "weight_gain":
      return {
        min: maintenance + 300,
        max: maintenance + 500,
        aggressive: maintenance + 750,
        maintenance,
      };
    default:
      return {
        min: maintenance - 100,
        max: maintenance + 100,
        maintenance,
      };
  }
};

/**
 * Get last N days of records
 */
export const getLastNDaysRecords = (records, days = 30) => {
  if (!records || !Array.isArray(records)) return [];

  const sortedRecords = [...records].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return sortedRecords.slice(0, days).reverse();
};

/**
 * Extract metric values from records
 */
export const extractMetricValues = (records, metric) => {
  return records.map((record) => record[metric] || 0);
};
